import {
  getBusinessApiClient,
  BusinessApiConflictError,
  BusinessApiError,
} from '@infra/api/BusinessApiClient';
import { RemoteSyncStore, type SyncOutboxRow } from '@infra/sync/RemoteSyncStore';
import { isBulletinSyncResource, resolveSyncConflict } from '@infra/sync/ConflictResolutionService';
import { logger } from '@infra/telemetry/Logger';
import { ToastService } from '@presentation/components/Toast';
import { AppContext } from '@application/context/AppContext';
import { syncEntitlementsAfterLocalSync } from '@application/licence/LicenceEntitlementSync';

export const SYNC_REJECTED_EVENT = 'nexxus:sync-rejected';

export interface SyncRunResult {
  pushed: number;
  conflicts: number;
  /** Entrées refusées définitivement et mises de côté pendant ce passage. */
  rejected?: number;
  /** true si la file s'est mise en pause (erreur transitoire) : reprise au retour réseau. */
  paused?: boolean;
}

type Api = ReturnType<typeof getBusinessApiClient>;

/** Classe de reprise : champ `retry` du serveur, sinon déduction prudente (jamais de perte). */
export function classifySyncError(err: unknown): 'permanent' | 'transient' | 'conflict' {
  if (err instanceof BusinessApiConflictError) return 'conflict';
  if (err instanceof BusinessApiError) {
    if (err.retry) return err.retry;
    if (err.code === 'version_conflict' || err.statusCode === 409) return 'conflict';
    if (err.statusCode === 429 || (err.statusCode ?? 0) >= 500) return 'transient';
    if (err.statusCode === 401) return 'transient';
  }
  // Réseau coupé, erreur inconnue : pause et nouvel essai plus tard.
  return 'transient';
}

async function pushEntry(api: Api, entry: SyncOutboxRow, versionOverride?: number): Promise<void> {
  const version = versionOverride ?? entry.base_version;
  if (entry.resource === 'tenant-profile') {
    await api.upsertTenantProfile(entry.payload, version);
  } else if (entry.resource === 'module-data') {
    await api.upsertModuleData(entry.entity_id, entry.payload, version);
  } else if (entry.action === 'create') {
    await api.createEntity(entry.resource, entry.payload);
  } else if (entry.action === 'update') {
    await api.updateEntity(entry.resource, entry.entity_id, entry.payload, version);
  } else if (entry.action === 'delete') {
    await api.deleteEntity(entry.resource, entry.entity_id);
  }
}

async function fetchRemote(
  api: Api,
  entry: SyncOutboxRow,
): Promise<{ payload: Record<string, unknown>; version: number } | null> {
  if (entry.resource === 'tenant-profile') return api.getTenantProfile();
  if (entry.resource === 'module-data') return api.getModuleData(entry.entity_id);
  return api.getEntity(entry.resource, entry.entity_id);
}

/** Dernière modification gagne : on rejoue avec la version serveur courante. */
async function resolveLastWriteWins(api: Api, entry: SyncOutboxRow): Promise<'applied' | 'locked'> {
  const remote = await fetchRemote(api, entry);
  if (remote && isBulletinSyncResource(entry.resource)) {
    const statut = remote.payload['statut'];
    if (statut === 'valide' || statut === 'paye') return 'locked';
  }
  await pushEntry(api, entry, remote?.version ?? entry.base_version);
  return 'applied';
}

async function parkRejected(entry: SyncOutboxRow, err: unknown): Promise<void> {
  const code = err instanceof BusinessApiError ? err.code : undefined;
  await RemoteSyncStore.markOutboxRejected(entry, code, rejectionMessage(code));
  logger.warn({ event: 'sync_entry_rejected', code: code ?? 'refused', resource: entry.resource });
}

/** Messages simples en français pour les refus définitifs. */
export function rejectionMessage(code: string | undefined): string {
  switch (code) {
    case 'module_not_licensed':
      return 'Ce module n’est pas inclus dans votre licence.';
    case 'insufficient_role':
    case 'forbidden_role':
      return 'Votre rôle ne permet pas cette modification.';
    case 'salary_limit_reached':
      return 'Nombre maximal de salariés de la licence atteint.';
    case 'user_limit_reached':
      return 'Nombre maximal d’utilisateurs de la licence atteint.';
    case 'license_revoked':
      return 'La licence a été révoquée.';
    case 'license_suspended':
      return 'La licence est suspendue : saisie bloquée, données consultables.';
    case 'license_expired':
      return 'La licence a expiré : saisie bloquée, données consultables.';
    case 'payslip_locked':
      return 'Bulletin validé ou payé : modification refusée (verrouillé).';
    default:
      return 'Modification refusée par le serveur.';
  }
}

/** Pousse la file sync_outbox et détecte les conflits distants. */
export class SyncCoordinator {
  private syncing = false;

  async syncWhenOnline(tenantId: string): Promise<SyncRunResult> {
    if (this.syncing) return { pushed: 0, conflicts: 0 };
    if (!(await RemoteSyncStore.isEnabled())) return { pushed: 0, conflicts: 0 };

    const api = getBusinessApiClient();
    const ready = await api.confirmWriteReady();
    if (!ready || !api.hasUserSession()) return { pushed: 0, conflicts: 0 };

    this.syncing = true;
    let pushed = 0;
    let conflicts = 0;

    let rejected = 0;
    let paused = false;

    try {
      const outbox = await RemoteSyncStore.listPendingOutbox(tenantId);
      for (const entry of outbox) {
        try {
          await pushEntry(api, entry);
          await RemoteSyncStore.removeOutbox(entry.id);
          pushed += 1;
        } catch (err) {
          const kind = classifySyncError(err);
          if (kind === 'conflict') {
            // Décision produit : la dernière modification gagne (bulletin validé verrouillé).
            try {
              const outcome = await resolveLastWriteWins(api, entry);
              if (outcome === 'locked') {
                await RemoteSyncStore.markOutboxRejected(
                  entry,
                  'bulletin_verrouille',
                  'Bulletin validé : modification refusée (verrouillé).',
                );
                rejected += 1;
              } else {
                await RemoteSyncStore.removeOutbox(entry.id);
                pushed += 1;
                conflicts += 1;
              }
              continue;
            } catch (retryErr) {
              if (classifySyncError(retryErr) === 'permanent') {
                await parkRejected(entry, retryErr);
                rejected += 1;
                continue;
              }
              logger.warn({ event: 'sync_push_failed', error: String(retryErr) });
              paused = true;
              break;
            }
          }
          if (kind === 'permanent') {
            // Refus définitif : on met l'entrée de côté et on continue avec les suivantes.
            await parkRejected(entry, err);
            rejected += 1;
            continue;
          }
          // Transitoire / inconnu : pause de la file, reprise au retour réseau (rien n'est perdu).
          logger.warn({ event: 'sync_push_failed', error: String(err) });
          paused = true;
          break;
        }
      }

      const pending = await RemoteSyncStore.listConflicts(tenantId);
      for (const conflict of pending) {
        const resolved = await resolveSyncConflict(conflict);
        if (resolved) {
          await RemoteSyncStore.clearConflict(tenantId, conflict.resource, conflict.entity_id);
          conflicts += 1;
        }
      }

      if (pushed > 0) {
        ToastService.success(`Synchronisation : ${pushed} modification(s) envoyée(s)`);
      }
      if (conflicts > 0) {
        ToastService.warning(`${conflicts} conflit(s) traité(s) — vérifiez vos données`);
      }
      if (rejected > 0) {
        ToastService.warning(
          `${rejected} modification(s) refusée(s) par le serveur et mise(s) de côté. Elles restent consultables.`,
        );
        window.dispatchEvent(
          new CustomEvent(SYNC_REJECTED_EVENT, { detail: { tenantId, rejected } }),
        );
      }
      await syncEntitlementsAfterLocalSync(AppContext.getInstance());
    } finally {
      this.syncing = false;
    }

    return {
      pushed,
      conflicts,
      ...(rejected > 0 ? { rejected } : {}),
      ...(paused ? { paused } : {}),
    };
  }

  async pullFreshCache(tenantId: string, resources: string[]): Promise<void> {
    if (!(await RemoteSyncStore.isEnabled())) return;
    const api = getBusinessApiClient();
    if (!(await api.confirmWriteReady())) return;
    for (const resource of resources) {
      if (resource === 'tenant-profile') {
        const profile = await api.getTenantProfile();
        if (profile) {
          await RemoteSyncStore.putCache(
            tenantId,
            resource,
            tenantId,
            profile.payload,
            profile.version,
            false,
          );
        }
        continue;
      }
      if (resource === 'module-data') {
        continue;
      }
      const rows = await api.listEntities(resource);
      for (const row of rows) {
        await RemoteSyncStore.putCache(tenantId, resource, row.id, row.payload, row.version, false);
      }
    }
  }
}

let coordinator: SyncCoordinator | null = null;

export function getSyncCoordinator(): SyncCoordinator {
  if (!coordinator) coordinator = new SyncCoordinator();
  return coordinator;
}
